// src/pages/FreshLeadDetail.jsx
import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import AppDataGrid from "../components/AppDataGrid";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { MasterDetailModule } from "ag-grid-enterprise";
import ReactDOM from "react-dom";
import DOMPurify from "dompurify";
import { BASE_URL } from "../utils";
import { FaPlus, FaMinus } from "react-icons/fa";
import { FiPaperclip, FiTrash2 } from "react-icons/fi";
import AcrisPartiesModal from "./AcrisPartiesModal";
import FamilyHistoryModal from "./FamilyHistoryModal"; // ✅ NEW
import { Edit2Icon } from "lucide-react";

ModuleRegistry.registerModules([AllCommunityModule, MasterDetailModule]);

function PortalModal({ open, title, children, onClose }) {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded shadow-lg w-full max-w-4xl p-4 max-h-[80vh] overflow-auto"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="px-2 py-1 border rounded text-sm">
            Close
          </button>
        </div>
        <div className="raw-html-preview prose max-w-full text-sm">{children}</div>
      </div>
    </div>,
    document.body
  );
}

function pad2(n) {
  return String(n).padStart(2, "0");
}
function formatDateMDY(dateLike) {
  if (!dateLike && dateLike !== 0) return null;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;
}

const truncate = (s, n = 20) => {
  if (s == null) return "";
  const str = String(s).trim();
  if (!str) return "";
  return str.length > n ? str.slice(0, n) : str;
};

const AddressCell = (props) => {
  const full = props.value == null ? "" : String(props.value).trim();
  const isLong = full.length > 20;
  const short = truncate(full, 20);

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 320 });

  React.useEffect(() => {
    if (!open) return;

    const updatePos = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({
        top: r.bottom + 8,
        left: Math.min(r.left, window.innerWidth - 460),
        width: Math.min(420, Math.max(260, r.width)),
      });
    };

    updatePos();

    const onDocMouseDown = (e) => {
      const el = anchorRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!full) return <span className="text-slate-400">—</span>;

  return (
    <div ref={anchorRef} className="w-full">
      <div className="flex items-center gap-2 w-full">
        <span className="block flex-1 min-w-0 truncate text-sm" style={{ userSelect: "text" }}>
          {isLong ? `${short}…` : full}
        </span>

        {isLong && (
          <button
            type="button"
            className="shrink-0 text-[11px] text-blue-600 underline hover:text-blue-700"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            {open ? "Hide" : "View more"}
          </button>
        )}
      </div>

      {open &&
        ReactDOM.createPortal(
          <div
            className="fixed z-[999999]"
            style={{ top: pos.top, left: pos.left }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="rounded-lg border border-slate-200 bg-white shadow-xl p-2 text-xs text-slate-800 whitespace-normal break-words w-[420px] max-w-[calc(100vw-24px)]">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="font-semibold text-slate-700">Full Address</div>
                <button
                  className="text-[11px] text-slate-500 hover:text-slate-800"
                  onClick={() => setOpen(false)}
                  type="button"
                >
                  ✕
                </button>
              </div>
              <div style={{ userSelect: "text" }}>{full}</div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

/* =========================================================
   ✅ SAME AS LegacyHome: latest-note helpers + StatusPill
   ========================================================= */
function noteTimeMs(note) {
  const v = note?.created_at || note?.updated_at || note?.timestamp || null;
  const d = v ? new Date(v) : null;
  const ms = d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  return ms;
}

function getLatestNote(row) {
  const notes = Array.isArray(row?.notes) ? row.notes : [];
  if (notes.length === 0) return null;
  const sorted = [...notes].sort((a, b) => noteTimeMs(a) - noteTimeMs(b));
  return sorted[sorted.length - 1] || null;
}

function StatusPill({ value }) {
  const raw = (value ?? "").toString().trim();
  const v = raw ? raw : "NA";
  const key = v.toLowerCase();

  const base =
    "inline-flex items-center justify-center px-2 py-[2px] rounded-full text-[11px] font-semibold border whitespace-nowrap";

  const cls =
    key === "approved"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-700/50"
      : key === "pending"
      ? "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/25 dark:text-amber-200 dark:border-amber-700/50"
      : key === "rejected"
      ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/25 dark:text-rose-200 dark:border-rose-700/50"
      : "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800/40 dark:text-slate-200 dark:border-slate-600/50";

  return <span className={`${base} ${cls}`}>{v}</span>;
}

export default function FreshLeadDetail() {
  const navigate = useNavigate();
  const { name } = useParams();
  const decodedName = useMemo(() => decodeURIComponent(name || ""), [name]);

  const gridRef = useRef(null);
  const mountedRef = useRef(true);

  const [rowData, setRowData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);

  const [gridHeight, setGridHeight] = useState(Math.max(320, window.innerHeight - 200));

  const sizeColumnsToFit = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.sizeColumnsToFit();
  }, []);

  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlModalContent, setHtmlModalContent] = useState("");
  const [htmlModalTitle, setHtmlModalTitle] = useState("Family History — HTML View");

  // ✅ Acris Parties modal
  const [partyModalOpen, setPartyModalOpen] = useState(false);
  const [partyModalRecord, setPartyModalRecord] = useState(null);

  // ✅ Family History modal
  const [familyModalOpen, setFamilyModalOpen] = useState(false);
  const [familyModalRecord, setFamilyModalRecord] = useState(null);

  useEffect(() => {
    function onResize() {
      setGridHeight(Math.max(320, window.innerHeight - 200));
      sizeColumnsToFit();
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [sizeColumnsToFit]);

  function escapeHtml(unsafe) {
    if (!unsafe) return "";
    return String(unsafe)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
  function stripHtml(html) {
    if (!html) return "";
    return String(html).replace(/<[^>]*>/g, "");
  }

  const showHtmlContent = useCallback((html, title = "HTML View") => {
    if (!html) return;

    let safe = String(html || "");

    // Remove scripts/styles and inline handlers
    safe = safe.replace(/<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi, "");
    safe = safe.replace(/\son\w+=(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");

    /* =========================================================
       ✅ ADD NUMBERING (UL → OL)
       ========================================================= */
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(safe, "text/html");

      const uls = doc.querySelectorAll("ul");
      uls.forEach((ul) => {
        const ol = doc.createElement("ol");

        // copy attributes
        for (const attr of ul.attributes) {
          ol.setAttribute(attr.name, attr.value);
        }

        // force numbering style
        const style = ol.getAttribute("style") || "";
        ol.setAttribute("style", `${style}; list-style-type: decimal; padding-left: 22px; margin: 6px 0;`);

        // move li children
        while (ul.firstChild) {
          ol.appendChild(ul.firstChild);
        }

        ul.replaceWith(ol);
      });

      safe = doc.body.innerHTML;
    } catch (e) {
      // if DOMParser fails, continue without numbering
    }

    // Sanitize final HTML
    try {
      safe = DOMPurify.sanitize(safe, {
        ADD_ATTR: ["target", "class", "style", "data-*"],
      });
    } catch (e) {
      safe = escapeHtml(stripHtml(safe));
    }

    setHtmlModalContent(safe);
    setHtmlModalTitle(title);
    setHtmlModalOpen(true);
  }, []);

  const buildPdfHref = (pdf_url) => {
    if (!pdf_url) return null;
    if (pdf_url.startsWith("http://") || pdf_url.startsWith("https://")) return pdf_url;
    const path = pdf_url.startsWith("/") ? pdf_url : `/${pdf_url}`;
    return `${BASE_URL}${path}`;
  };

  function NoteItem({ note, caseId, onUpdate, onDelete }) {
    const [isEditingInline, setIsEditingInline] = useState(false);
    const [editedText, setEditedText] = useState(note.note || note.text || "");

    useEffect(() => {
      setEditedText(note.note || note.text || "");
    }, [note.note, note.text]);

    const pdfHref = buildPdfHref(note.pdf_url || note.pdfUrl || note.url || "");

    return (
      <div className="text-sm mb-3 pl-2 border-l-4 border-gray-300">
        {isEditingInline ? (
          <>
            <textarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              className="text-xs border px-2 py-1 rounded w-full"
            />
            <div className="flex gap-2 mt-1">
              <button
                onClick={async () => {
                  if (!editedText.trim()) return alert("Note cannot be empty!");
                  if (onUpdate) await onUpdate(editedText.trim());
                  setIsEditingInline(false);
                }}
                className="text-green-600 text-xs"
              >
                💾 Save
              </button>
              <button onClick={() => setIsEditingInline(false)} className="text-gray-500 text-xs">
                ❌ Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-gray-800 whitespace-pre-line mb-2" dangerouslySetInnerHTML={{ __html: note.note || note.text || "" }} />
            {pdfHref && (
              <div className="mb-2">
                <a
                  href={pdfHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 underline inline-flex items-center gap-1"
                >
                  <FiPaperclip size={12} /> View Attached PDF
                </a>
              </div>
            )}
            <div className="text-[11px] text-gray-500 flex items-center justify-between">
              <span>{note.created_at ? new Date(note.created_at).toLocaleString() : note.timestamp || ""}</span>
              <div className="flex gap-3 items-center">
                <button onClick={() => setIsEditingInline(true)} className="text-indigo-600 text-xs">
                  ✏️ Edit
                </button>
                <button onClick={() => onDelete && onDelete()} className="text-red-500 text-xs">
                  <FiTrash2 size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  const DetailCellRenderer = (props) => {
    const row = props.data || {};
    const notes = Array.isArray(row.notes) ? row.notes : [];
    const latest = getLatestNote(row);

    const latestStatus = latest?.status || "NA";
    const latestSubStatus = latest?.sub_status || latest?.subStatus || "NA";
    const latestLabel = latest?.label || "NA";
    const latestFollowUp = latest?.followUp_date || latest?.followup_date || latest?.followUpDate || null;

    const deleteNote = async (note) => {
      const nid = note.id || note.noteId || note.note_id;
      if (!nid) return;
      if (!window.confirm("Delete this note?")) return;
      try {
        await axios.delete(`${BASE_URL}/api/legacy/note/${nid}`).catch(async () => {
          await axios.delete(`${BASE_URL}/api/legacy/${row.id}/notes/${nid}`);
        });
        fetchData(page, limit);
      } catch (err) {
        console.error("delete note error", err);
        alert("Failed to delete note");
      }
    };

    const updateNote = async (note, newText) => {
      const nid = note.id || note.noteId || note.note_id;
      if (!nid) return;
      try {
        await axios.put(`${BASE_URL}/api/legacy/${row.id}/notes/${nid}`, {
          note: newText,
          plain_text: stripHtml(newText),
        });
        await fetchData(page, limit);
      } catch (err) {
        console.error("update note error", err);
        alert("Failed to update note");
      }
    };

    return (
      <div className="p-2 text-xs bg-[var(--surface-muted)]" style={{ whiteSpace: "pre-wrap", overflow: "visible" }}>
        {/* ✅ SAME header as LegacyHome */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <div className="flex items-center gap-2">
            <strong>Status:</strong> <StatusPill value={latestStatus} />
          </div>
          <div>
            <strong>Sub-Status:</strong> {latestSubStatus || "NA"}
          </div>
          <div>
            <strong>Label:</strong> {latestLabel || "NA"}
          </div>
          <div>
            <strong>Follow Up:</strong> {latestFollowUp ? formatDateMDY(latestFollowUp) || "—" : "—"}
          </div>
        </div>

        <div>
          <strong>Notes:</strong>
        </div>
        <div className="mt-1">
          {notes.length === 0 && <div className="text-xs text-gray-500">No notes</div>}
          {notes.map((n) => (
            <NoteItem
              key={n.id || n.noteId || n.timestamp || Math.random()}
              note={n}
              caseId={row.id}
              onUpdate={(newText) => updateNote(n, newText)}
              onDelete={() => deleteNote(n)}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderTextOrDash = (val) => {
    if (val === null || val === undefined) return "—";
    const s = String(val).trim();
    return s === "" ? "—" : s;
  };

  const buildCaseId = (data) => {
    if (!data) return "—";
    const b = data.property_borough ?? data.property_borough_name ?? "";
    const block = data.property_block ?? "";
    const lot = data.property_lot ?? "";
    if (![b, block, lot].some((x) => x !== null && x !== undefined && String(x).trim() !== "")) return "—";
    const parts = [b, block, lot].map((p) => (p == null ? "" : String(p).trim()));
    return `${parts[0] || ""}-${parts[1] || ""}-${parts[2] || ""}`.replace(/^-+|-+$/g, "").replace(/-{2,}/g, "-");
  };

  const fetchData = useCallback(
    async (p = 1, pageSize = limit) => {
      try {
        setLoading(true);
        gridRef.current?.api?.showLoadingOverlay?.();

        const res = await axios.get(`${BASE_URL}/api/legacy/fresh-leads`, {
          params: { name: decodedName, page: p, limit: pageSize },
        });

        if (!mountedRef.current) return;

        const rows = res.data?.data || [];
        setRowData(rows);

        const meta = res.data?.meta || {};
        setTotal(Number(meta.total || 0));
        setPage(Number(meta.page || p));
        setLimit(Number(meta.limit || pageSize));

        setTimeout(() => gridRef.current?.api?.hideOverlay?.(), 80);
        setTimeout(() => sizeColumnsToFit(), 100);
      } catch (err) {
        console.error("fetch fresh leads detail", err);
        try {
          gridRef.current?.api?.showNoRowsOverlay?.();
        } catch (e) {}
      } finally {
        setLoading(false);
      }
    },
    [limit, decodedName, sizeColumnsToFit]
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchData(1, limit);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData, limit]);

  const handleDeleteRecord = async (id) => {
    if (!id) return;
    // ✅ this will call your backend soft delete now (isDeleted=1)
    if (!window.confirm("Delete this record?")) return;
    try {
      await axios.delete(`${BASE_URL}/api/legacy/${id}`);
      await fetchData(page, limit);
    } catch (e) {
      console.error("delete record error", e);
      alert("Failed to delete record");
    }
  };

  const handleConfirmRecord = async (id) => {
    if (!id) return;
    try {
      await axios.post(`${BASE_URL}/api/legacy/${id}/confirm`);
      await fetchData(page, limit);
    } catch (e) {
      console.error("confirm record error", e);
      alert("Failed to confirm record");
    }
  };

  const isConfirmedRow = (row) => {
    const v = row?.confirmed ?? row?.is_confirmed ?? row?.isConfirmed ?? 0;
    return Number(v) === 1;
  };

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Case id",
        field: "case_id",
        width: 120,
        minWidth: 100,
        sortable: false,
        valueGetter: (p) => buildCaseId(p.data),
        cellRenderer: (params) => {
          const val = buildCaseId(params.data);
          if (!params.data || val === "—") return "—";
          const id = params.data?.id || "";
          const href = id ? `/legacy/${id}` : "#";
          return (
            <a href={href} target="_blank" rel="noreferrer" className="text-blue-600 underline" title="Open details">
              {val}
            </a>
          );
        },
      },
      {
        headerName: "Full Name",
        field: "full_name",
        width: 160,
        minWidth: 140,
        tooltipField: "full_name",
        cellRenderer: (p) => {
          const v = renderTextOrDash(p.value);
          return v === "—" ? "—" : <span className="text-sm">{String(v)}</span>;
        },
      },
      {
        headerName: "Date of death",
        field: "date_of_death",
        width: 180,
        minWidth: 180,
        valueGetter: (p) => p.data?.obituary_published_date || null,
        cellRenderer: (p) => (p.value ? formatDateMDY(p.value) || "—" : "—"),
      },
      {
        headerName: "Address",
        field: "property_full_address",
        width: 220,
        minWidth: 160,
        cellRenderer: AddressCell,
      },
      { headerName: "City", field: "property_city", width: 120, minWidth: 100, cellRenderer: (p) => renderTextOrDash(p.value) },

      /* ✅ NEW: Status column same as LegacyHome */
      {
        headerName: "Status",
        field: "__latest_status",
        width: 120,
        minWidth: 120,
        sortable: true,
        valueGetter: (p) => getLatestNote(p.data)?.status || "NA",
        cellRenderer: (p) => <StatusPill value={p.value} />,
        tooltipValueGetter: (p) => getLatestNote(p.data)?.status || "NA",
      },

      {
        headerName: "Family History (html)",
        field: "family_history_html",
        minWidth: 200,
        width: 200,
        cellRenderer: (params) => {
          const htmlVal = params.value ?? "";
          const raw = String(htmlVal).trim();
          const hasHtml = raw && raw.toLowerCase() !== "na";

          if (hasHtml) {
            return (
              <button
                className="p-1 rounded hover:bg-gray-100 ag-icon-btn text-blue-600"
                title="View HTML"
                aria-label="View HTML"
                onClick={(e) => {
                  e.stopPropagation();
                  showHtmlContent(htmlVal, `Family History — ${params.data?.full_name || ""}`);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </button>
            );
          }

          return renderTextOrDash(null);
        },
      },
      {
        headerName: "Block",
        field: "property_block",
        width: 100,
        minWidth: 100,
        cellRenderer: (p) => renderTextOrDash(p.value),
      },
      {
        headerName: "Lot",
        field: "property_lot",
        width: 90,
        minWidth: 90,
        cellRenderer: (p) => renderTextOrDash(p.value),
      },
      {
        headerName: "State",
        field: "property_state",
        width: 120,
        cellRenderer: (p) => {
          const v = p.value ?? p.data?.property_state ?? "";
          const t = String(v).trim();
          return t === "" ? "New York" : t;
        },
      },
      {
        headerName: "Notes",
        field: "notes",
        width: 120,
        minWidth: 120,
        cellRenderer: (params) => {
          const notes = Array.isArray(params.data?.notes) ? params.data.notes : [];
          const count = notes.length;
          const isExpanded = !!params.node?.expanded;
          return (
            <div className="w-full flex items-center justify-center">
              <div className="flex items-center gap-2">
                <span className="text-xs">{count > 0 ? `${count}` : "0"}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const node = params.node;
                    const willOpen = !node.expanded;
                    params.api.forEachNode((n) => n && n.expanded && n.setExpanded(false));
                    node.setExpanded(willOpen);
                    params.api.refreshCells({ force: true });
                    params.api.resetRowHeights();
                  }}
                  title={isExpanded ? "Collapse" : "Expand"}
                  className={`w-5 h-5 rounded-full flex items-center justify-center shadow-md border ${
                    isExpanded ? "bg-red-500 text-white" : "bg-blue-600 text-white"
                  }`}
                >
                  {isExpanded ? <FaMinus size={10} /> : <FaPlus size={10} />}
                </button>
              </div>
            </div>
          );
        },
      },

      // ✅ UPDATED ACTION COLUMN (Edit Parties + Edit Family)
      {
        headerName: "Action",
        field: "__action",
        width: 190,
        minWidth: 150,
        sortable: false,
        filter: false,
        cellRenderer: (params) => {
          const row = params.data || {};
          const id = row?.id;
          const confirmed = isConfirmedRow(row);

          return (
            <div className="flex items-center justify-center gap-2">
              <button
                className="ui-btn text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!id) return;
                  setFamilyModalRecord(row);
                  setFamilyModalOpen(true);
                }}
                title="Edit Family"
              >
                <Edit2Icon size={14} />
              </button>

              <button
                className="ui-btn text-xs text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteRecord(id);
                }}
                title="Delete"
              >
                <FiTrash2 size={14} />
              </button>

              {confirmed ? (
                <span className="confirm-pill confirm-pill--confirmed">Confirmed</span>
              ) : (
                <button
                  type="button"
                  className="confirm-pill confirm-pill--action"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfirmRecord(id);
                  }}
                >
                  Confirm
                </button>
              )}
            </div>
          );
        },
      },
    ];
  }, [showHtmlContent, page, limit]);

  function onGridReady(params) {
    try {
      if (!gridRef.current) gridRef.current = {};
      gridRef.current.api = params.api;
      gridRef.current.columnApi = params.columnApi;
      sizeColumnsToFit();
    } catch (e) {}
  }

  const totalPages = Math.max(1, Math.ceil(total / limit || 1));
  const startRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const endRow = Math.min(total, page * limit);
  const goPage = (p) => fetchData(Math.max(1, Math.min(totalPages, p)), limit);

  return (
    <div className="p-4 flex flex-col min-h-[80vh] min-w-0">
      <div className="filter-panel mb-4 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="page-kicker">Legacy</div>
          <h2 className="page-title">Fresh Lead Detail</h2>
          <div className="page-subtitle">
            Name: <span className="font-semibold">{decodedName}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/legacy/fresh-leads")} className="ui-btn">
            ← Back
          </button>

          <select
            value={limit}
            onChange={(e) => {
              const n = Number(e.target.value);
              setLimit(n);
              fetchData(1, n);
            }}
            className="ui-select"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      <div className="rounded-lg border flex-1 min-w-0 overflow-hidden">
        <AppDataGrid
          gridRef={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          height={gridHeight}
          minWidth={900}
          rowHeight={42}
          headerHeight={36}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
            minWidth: 80,
            cellClass: "text-sm text-center",
            headerClass: "text-center",
            cellStyle: { userSelect: "text" },
          }}
          enableCellTextSelection={true}
          animateRows={true}
          onGridReady={onGridReady}
          overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading data...</span>'
          overlayNoRowsTemplate='<span class="ag-overlay-loading-center">No records to display</span>'
          masterDetail={true}
          isRowMaster={() => true}
          detailCellRenderer={DetailCellRenderer}
          detailRowAutoHeight={true}
          detailRowHeight={220}
          pagination={false}
          domLayout="normal"
          getRowId={(params) => (params.data?.id ? String(params.data.id) : `${Math.random()}`)}
          loading={loading}
        />
      </div>

      <div className="flex items-center justify-between gap-3 mt-4">
        <div className="text-sm text-slate-600">
          {total === 0 ? (
            <>No records</>
          ) : (
            <>
              Showing <strong>{startRow}</strong>–<strong>{endRow}</strong> of <strong>{total}</strong>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => goPage(1)} disabled={page <= 1} className="px-3 py-1 border rounded disabled:opacity-60 text-sm">
            « First
          </button>
          <button onClick={() => goPage(page - 1)} disabled={page <= 1} className="px-3 py-1 border rounded disabled:opacity-60 text-sm">
            ◀ Prev
          </button>
          <div className="text-sm text-slate-600">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </div>
          <button onClick={() => goPage(page + 1)} disabled={page >= totalPages} className="px-3 py-1 border rounded disabled:opacity-60 text-sm">
            Next ▶
          </button>
          <button onClick={() => goPage(totalPages)} disabled={page >= totalPages} className="px-3 py-1 border rounded disabled:opacity-60 text-sm">
            Last »
          </button>
        </div>
      </div>

      {/* Family history HTML viewer */}
      <PortalModal open={htmlModalOpen} title={htmlModalTitle} onClose={() => setHtmlModalOpen(false)}>
        <div dangerouslySetInnerHTML={{ __html: htmlModalContent }} />
      </PortalModal>

      <AcrisPartiesModal
        open={partyModalOpen}
        record={partyModalRecord}
        onClose={() => {
          setPartyModalOpen(false);
          setPartyModalRecord(null);
        }}
        onSaved={() => fetchData(page, limit)}
      />

      {/* ✅ Family History editor */}
      <FamilyHistoryModal
        open={familyModalOpen}
        record={familyModalRecord}
        onClose={() => {
          setFamilyModalOpen(false);
          setFamilyModalRecord(null);
        }}
        onSaved={() => fetchData(page, limit)}
      />
    </div>
  );
}
